import api from './api';
import {
  JobOffer,
  JobOffersListResponse,
  CreateJobOfferRequest,
  UpdateJobOfferRequest,
  JobOfferResponse,
  RespondToJobOfferRequest,
  JobOfferStatus,
} from '@/types';

export interface JobOfferFilters {
  status?: JobOfferStatus | JobOfferStatus[];
  candidateId?: string;
  jobDescriptionId?: string;
  page?: number;
  limit?: number;
  sortBy?: 'createdAt' | 'respondedAt' | 'expiresAt';
  sortOrder?: 'asc' | 'desc';
}

function buildParams(filters?: JobOfferFilters) {
  if (!filters) return undefined;
  // axios serialises arrays as status[]=… by default; the API expects
  // repeated params (status=…&status=…), so we use URLSearchParams directly.
  const { status, ...rest } = filters;
  const params = new URLSearchParams();
  Object.entries(rest).forEach(([k, v]) => {
    if (v !== undefined && v !== null) params.append(k, String(v));
  });
  if (status !== undefined) {
    (Array.isArray(status) ? status : [status]).forEach((s) => params.append('status', s));
  }
  return params;
}

// Recruiter endpoints
export const recruiterJobOfferService = {
  // Create a job offer
  async createJobOffer(data: CreateJobOfferRequest): Promise<JobOffer> {
    const response = await api.post('/v1/job-offers', data);
    return response.data;
  },

  // List sent job offers
  async listJobOffers(filters?: JobOfferFilters): Promise<JobOffersListResponse> {
    const response = await api.get('/v1/job-offers', { params: buildParams(filters) });
    return response.data;
  },

  // Get job offer details
  async getJobOfferDetails(offerId: string): Promise<JobOffer> {
    const response = await api.get(`/v1/job-offers/${offerId}`);
    return response.data;
  },

  // Update job offer
  async updateJobOffer(offerId: string, data: UpdateJobOfferRequest): Promise<JobOffer> {
    const response = await api.patch(`/v1/job-offers/${offerId}`, data);
    return response.data;
  },

  // Withdraw job offer
  async withdrawJobOffer(offerId: string): Promise<JobOfferResponse> {
    const response = await api.delete(`/v1/job-offers/${offerId}`);
    return response.data;
  },
};

// Candidate endpoints
export const candidateJobOfferService = {
  // List received job offers
  async listJobOffers(filters?: JobOfferFilters): Promise<JobOffersListResponse> {
    const response = await api.get('/v1/candidates/job-offers', { params: buildParams(filters) });
    return response.data;
  },

  // Get job offer details
  async getJobOfferDetails(offerId: string): Promise<JobOffer> {
    const response = await api.get(`/v1/candidates/job-offers/${offerId}`);
    return response.data;
  },

  // Accept job offer
  async acceptJobOffer(offerId: string, data?: RespondToJobOfferRequest): Promise<JobOfferResponse> {
    const response = await api.post(`/v1/candidates/job-offers/${offerId}/accept`, data);
    return response.data;
  },

  // Reject job offer
  async rejectJobOffer(offerId: string, data?: RespondToJobOfferRequest): Promise<JobOfferResponse> {
    const response = await api.post(`/v1/candidates/job-offers/${offerId}/reject`, data);
    return response.data;
  },
};

export default {
  recruiter: recruiterJobOfferService,
  candidate: candidateJobOfferService,
};
